<template>
  <div class="dk-ppi-root" :style="previewStyle">
    <!-- Trial expired: block player -->
    <div v-if="isTrialExpired" class="dk-ppi-blocked">
      <div class="dk-ppi-blocked__inner">
        <svg width="28" height="28" fill="none" viewBox="0 0 24 24">
          <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="2"/>
          <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <span>Player bloqueado</span>
        <a :href="kiwifyCheckoutUrl" target="_blank" class="dk-ppi-blocked__btn">Fazer Upgrade</a>
      </div>
    </div>
    <template v-else>
      <iframe
        :key="iframeKey"
        :src="embedSrc"
        frameborder="0"
        allow="autoplay; encrypted-media"
        allowfullscreen
        class="dk-ppi-frame"
      />
      <div v-if="loadError" class="dk-ppi-error">
        <svg width="36" height="36" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#EF4444" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="#EF4444" stroke-width="2" stroke-linecap="round"/></svg>
        <p class="dk-ppi-error__title">Vídeo não pôde ser carregado</p>
        <p class="dk-ppi-error__sub">A URL do arquivo no S3 está inválida. Refaça o upload deste vídeo.</p>
      </div>
      <button class="dk-ppi-refresh" @click="refresh" title="Atualizar preview">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="23 4 23 10 17 10"/>
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
        </svg>
        Atualizar
      </button>
    </template>
  </div>
</template>

<script>
import trialMixin from '@/mixins/trialMixin'

export default {
  name: 'PlayerPreviewIframe',
  mixins: [trialMixin],
  props: {
    vslId:       { type: String, required: true },
    thumbSrc:    { type: String, default: '' },
    orientation: { type: String, default: 'Horizontal' },
  },
  data() {
    return {
      iframeKey: 0,
      videoRatio: null,
      loadError: false,
      _loadTimer: null,
    }
  },
  computed: {
    embedSrc() {
      const origin = (process.env.VUE_APP_HOST_TEMPLATE || '').replace(/\/$/, '') || window.location.origin
      const base = `${origin}/player-preview/${this.vslId}`
      return this.thumbSrc
        ? `${base}?thumb=${encodeURIComponent(this.thumbSrc)}`
        : base
    },
    isVertical() {
      return (this.orientation || '').toLowerCase() === 'vertical'
    },
    previewStyle() {
      // Se já recebemos as dimensões reais do vídeo, usamos o ratio exato
      if (this.videoRatio) {
        const ratio = this.isVertical
          ? Math.min(this.videoRatio, 1)   // vídeo vertical: limita largura
          : this.videoRatio
        if (this.isVertical) {
          return { aspectRatio: `${ratio}`, maxWidth: '320px', margin: '0 auto' }
        }
        return { aspectRatio: `${ratio}` }
      }
      // Fallback enquanto o vídeo ainda não carregou
      if (this.isVertical) {
        return { aspectRatio: '9 / 16', maxWidth: '320px', margin: '0 auto' }
      }
      return { aspectRatio: '16 / 9' }
    },
  },
  mounted() {
    window.addEventListener('message', this._onVideoMessage)
    this._startTimeout()
  },
  beforeDestroy() {
    window.removeEventListener('message', this._onVideoMessage)
    if (this._loadTimer) clearTimeout(this._loadTimer)
  },
  methods: {
    _onVideoMessage(event) {
      if (event.data && event.data.type === 'dkp-video-dimensions' && event.data.w && event.data.h) {
        this.videoRatio = event.data.w / event.data.h
        this.loadError = false
        if (this._loadTimer) clearTimeout(this._loadTimer)
      }
    },
    _startTimeout() {
      if (this._loadTimer) clearTimeout(this._loadTimer)
      this.loadError = false
      this._loadTimer = setTimeout(() => {
        if (!this.videoRatio) this.loadError = true
      }, 8000)
    },
    refresh() {
      this.videoRatio = null
      this.loadError = false
      this.iframeKey += 1
      this._startTimeout()
    },
  },
}
</script>

<style scoped>
.dk-ppi-root {
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  transition: aspect-ratio 0.15s ease;
}
.dk-ppi-frame {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}
.dk-ppi-refresh {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(0,0,0,0.55);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background 0.15s;
  z-index: 10;
}
.dk-ppi-refresh:hover {
  background: rgba(232,106,16,0.75);
  border-color: #E86A10;
}

.dk-ppi-blocked {
  width: 100%;
  height: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e, #16213e);
}

.dk-ppi-blocked__inner {
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px;
}

.dk-ppi-blocked__inner span {
  font-size: 0.9rem;
  font-weight: 600;
}

.dk-ppi-blocked__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 22px;
  background: linear-gradient(135deg, #E86A10, #f08535);
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.15s;
  margin-top: 6px;
}

.dk-ppi-blocked__btn:hover {
  background: linear-gradient(135deg, #cf5e0d, #E86A10);
  transform: translateY(-1px);
  color: #fff;
}

.dk-ppi-error {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.95);
  color: #fff;
  text-align: center;
  padding: 24px;
  gap: 8px;
  z-index: 5;
}
.dk-ppi-error__title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 8px 0 0;
}
.dk-ppi-error__sub {
  font-size: 0.78rem;
  opacity: 0.7;
  margin: 0;
  max-width: 300px;
}
</style>
